<script setup lang="ts">
import type { IProductGalleryImage } from '@/types/product-image.types';

const props = withDefaults(
  defineProps<{
    items: IProductGalleryImage[];
    pendingDeleteId: string | null;
    reordering: boolean;
    readonly?: boolean;
  }>(),
  { readonly: false },
);

const emit = defineEmits<{
  'move-up': [id: string];
  'move-down': [id: string];
  remove: [image: IProductGalleryImage];
}>();

function isFirst(id: string): boolean {
  return props.items[0]?.id === id;
}
function isLast(id: string): boolean {
  return props.items[props.items.length - 1]?.id === id;
}
</script>

<template>
  <div class="pgg">
    <div v-for="image in items" :key="image.id" class="pgg__item">
      <img :src="image.urls.small" class="pgg__img" alt="" />
      <span v-if="isFirst(image.id)" class="pgg__main-badge">Principale</span>
      <div v-if="!readonly" class="pgg__overlay">
        <button
          class="pgg__icon-btn"
          :disabled="reordering || isFirst(image.id)"
          title="Monter"
          @click="emit('move-up', image.id)"
        >
          <i class="pi pi-arrow-up" />
        </button>
        <button
          class="pgg__icon-btn"
          :disabled="reordering || isLast(image.id)"
          title="Descendre"
          @click="emit('move-down', image.id)"
        >
          <i class="pi pi-arrow-down" />
        </button>
        <button
          class="pgg__icon-btn pgg__icon-btn--danger"
          :disabled="pendingDeleteId === image.id"
          title="Supprimer"
          @click="emit('remove', image)"
        >
          <i
            v-if="pendingDeleteId === image.id"
            class="pi pi-spin pi-spinner"
          />
          <i v-else class="pi pi-trash" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pgg {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.875rem;
}
.pgg__item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
}
.pgg__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pgg__main-badge {
  position: absolute;
  left: 0.5rem;
  top: 0.5rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 9999px;
  background: rgba(11, 19, 32, 0.75);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.pgg__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.55) 0%,
    rgba(0, 0, 0, 0) 60%
  );
  opacity: 0;
  transition: opacity 0.15s ease;
}
.pgg__item:hover .pgg__overlay,
.pgg__item:focus-within .pgg__overlay {
  opacity: 1;
}
.pgg__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #0b1320;
  font-size: 0.75rem;
  cursor: pointer;
  flex-shrink: 0;
}
.pgg__icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pgg__icon-btn--danger {
  color: var(--nxt-error);
}
</style>
